import React, { useMemo } from "react";
import { Grid, Tooltip, Typography } from "@material-ui/core";
import paths from "@icarius-router/paths";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    getExtraHours,
    getMyActionsAmount,
    getFeedbackAmount,
    getHoursChangedAmount,
    getMostVisitedPages,
    getCommunicationsAmount,
    getHoursChangedMinDate,
    getPendingCommunicationsAmount,
    getOnboardingTrackingAmount,
    getTrainingAmount,
} from "@icarius-pages/myDesktop/selectors";
import { getAvailablePaths } from "@icarius-app/selectors";
import MyDigitalFolderIcon from "./myDigitalFolderIcon";
import MyLoansIcon from "./myLoansIcon";
import MyScheduleIcon from "./myScheduleIcon";
import AnnotationAnalysisIcon from "./annotationAnalysisIcon";
import MyBenefitsIcon from "./myBenefitsIcon";
import MyVacationsIcon from "./myVacationsIcon";
import AbsentCollaboratorsIcon from "./absentCollaboratorsIcon";
import OnboardingTrackingIcon from "./onboardingTrackingIcon";
import ScheduleChangeRequestsIcon from "./scheduleChangeRequestsIcon";
import MyActionsIcon from "./myActionsIcon";
import ExtraHoursAuthIcon from "./extraHoursAuthIcon";
import FeedbackIcon from "./feedbackIcon";
import CommunicationChannelIcon from "./communicationChannelIcon";
import PendingCommunicationChannelIcon from "./pendingCommunicationChannelIcon";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import { getLocalizedString } from "@icarius-localization/strings";
import useIsPathAvailable from "@icarius-utils/hooks/useIsPathAvailable";

const ICON_SIZE = 30;

const EasyAccess = (props) => {
    const {
        handleOpenMyCertificatesDialog,
        handleOpenMyReceiptsDialog,
        handleOpenMyDigitalDocumentsDialog,
        handleOpenRequestDocumentsDialog,
        handleOpenMyVacationsDialog,
        handleOpenAbsentCollaboratorsDialog,
        userRole,
    } = props;

    const history = useHistory();
    const mostVisitedPages = useSelector(getMostVisitedPages);
    const availablePaths = useSelector(getAvailablePaths);
    const extraHours = useSelector(getExtraHours);
    const myActionsAmount = useSelector(getMyActionsAmount);
    const feedbackAmount = useSelector(getFeedbackAmount);
    const communicationsAmount = useSelector(getCommunicationsAmount);
    const pendingCommunicationsAmount = useSelector(getPendingCommunicationsAmount);
    const hoursChangedAmount = useSelector(getHoursChangedAmount);
    const hoursChangedMinDate = useSelector(getHoursChangedMinDate);
    const onboardingTrackingAmount = useSelector(getOnboardingTrackingAmount);
    const trainingAmount = useSelector(getTrainingAmount);
    const myDesktopLoansButton = useIsPathAvailable(paths.myDesktopLoansButton);

    const dataByRole = useMemo(() => {
        return {
            C: [
                {
                    name: getLocalizedString("myVacations"),
                    icon: <MyVacationsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myVacations,
                },
                {
                    name: getLocalizedString("myAnnexesTitle"),
                    path: paths.myDigitalFolder,
                    icon: <MyDigitalFolderIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                },
                {
                    name: getLocalizedString("mySchedule"),
                    icon: <MyScheduleIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.mySchedule,
                },
                {
                    name: getLocalizedString("myLoans"),
                    icon: <MyLoansIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.loans,
                },
                {
                    name: getLocalizedString("myBenefits"),
                    icon: <MyBenefitsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myBenefits,
                },
                {
                    name: getLocalizedString("actions"),
                    icon: <MyActionsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.actions,
                    amount: myActionsAmount,
                },
                {
                    name: getLocalizedString("myFeedbacks"),
                    icon: <FeedbackIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.feedback,
                    amount: feedbackAmount,
                },
                {
                    name: getLocalizedString("myTraining"),
                    icon: <MenuBookIcon style={{ fill: "var(--mainText)", width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myTraining,
                    amount: trainingAmount,
                },
                {
                    name: getLocalizedString("myCommunicationChannel"),
                    icon: <CommunicationChannelIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.communicationChannel,
                    amount: communicationsAmount,
                    onClick: () => {
                        history.push({
                            pathname: paths.communicationChannel,
                            state: { pendingCommunications: false },
                        });
                    },
                },
                {
                    name: getLocalizedString("myOnboardingTracking"),
                    icon: <OnboardingTrackingIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myOnboardingTracking,
                    amount: onboardingTrackingAmount,
                },
            ],
            M: [
                {
                    name: getLocalizedString("myPeopleVacations"),
                    icon: <MyVacationsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.vacations,
                },
                {
                    name: getLocalizedString("myPeopleAnnotations"),
                    path: paths.annotationsAnalysis,
                    icon: <AnnotationAnalysisIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                },
                {
                    name: getLocalizedString("myPeopleTime"),
                    icon: <MyScheduleIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myPeopleSchedule,
                    onClick: () => {
                        const stateToSend = { from: paths.myDesktop };

                        history.push({
                            pathname: paths.myPeopleSchedule,
                            state: stateToSend,
                        });
                    },
                },
                {
                    name: getLocalizedString("extraHoursAuthorization"),
                    icon: <ExtraHoursAuthIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.extraHoursAuthorization,
                    amount: extraHours,
                },
                {
                    name: getLocalizedString("timeChangeRequests"),
                    path: paths.myPeopleSchedule,
                    amount: hoursChangedAmount,
                    icon: <ScheduleChangeRequestsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    onClick: () => {
                        const stateToSend = {
                            setRequestFilter: true,
                            from: paths.myDesktop,
                            date: hoursChangedMinDate,
                        };

                        history.push({
                            pathname: paths.myPeopleSchedule,
                            state: stateToSend,
                        });
                    },
                },
                {
                    name: getLocalizedString("actions"),
                    icon: <MyActionsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.actions,
                    amount: myActionsAmount,
                },
                {
                    name: getLocalizedString("myFeedbacks"),
                    icon: <FeedbackIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.feedback,
                    amount: feedbackAmount,
                },
                {
                    name: getLocalizedString("lms"),
                    icon: <MenuBookIcon style={{ fill: "var(--mainText)", width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myTraining,
                    amount: trainingAmount,
                },
                {
                    name: getLocalizedString("myCreatedCommunications"),
                    icon: <CommunicationChannelIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.communicationChannel,
                    amount: communicationsAmount,
                    onClick: () => {
                        history.push({
                            pathname: paths.communicationChannel,
                            state: { pendingCommunications: false },
                        });
                    },
                },
                {
                    name: getLocalizedString("communicationsToAttend"),
                    icon: <PendingCommunicationChannelIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.communicationChannel,
                    amount: pendingCommunicationsAmount,
                    onClick: () => {
                        history.push({
                            pathname: paths.communicationChannel,
                            state: { pendingCommunications: true },
                        });
                    },
                },
                {
                    name: getLocalizedString("myOnboardingTasks"),
                    icon: <OnboardingTrackingIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.onboardingTracking,
                    amount: onboardingTrackingAmount,
                },
                {
                    name: getLocalizedString("absentCollaborators"),
                    icon: <AbsentCollaboratorsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    onClick: handleOpenAbsentCollaboratorsDialog,
                },
            ],
            E: [
                {
                    name: getLocalizedString("actions"),
                    icon: <MyActionsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.actions,
                    amount: myActionsAmount,
                },
                {
                    name: getLocalizedString("myFeedbacks"),
                    icon: <FeedbackIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.feedback,
                    amount: feedbackAmount,
                },
                {
                    name: getLocalizedString("training"),
                    icon: <MenuBookIcon style={{ fill: "var(--mainText)", width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.myTraining,
                    amount: trainingAmount,
                },
                {
                    name: getLocalizedString("communicationChannel"),
                    icon: <CommunicationChannelIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    path: paths.communicationChannel,
                    amount: communicationsAmount,
                    onClick: () => {
                        history.push({
                            pathname: paths.communicationChannel,
                            state: { pendingCommunications: false },
                        });
                    },
                },
                {
                    name: getLocalizedString("absentCollaborators"),
                    icon: <AbsentCollaboratorsIcon style={{ width: ICON_SIZE, height: ICON_SIZE }} />,
                    onClick: handleOpenAbsentCollaboratorsDialog,
                },
            ],
        };
    }, [
        extraHours,
        myActionsAmount,
        feedbackAmount,
        communicationsAmount,
        pendingCommunicationsAmount,
        hoursChangedAmount,
        hoursChangedMinDate,
        history,
        onboardingTrackingAmount,
        trainingAmount,
        handleOpenAbsentCollaboratorsDialog,
    ]);

    const dataToUse = useMemo(() => {
        let auxData = [...dataByRole[userRole], ...mostVisitedPages];

        if (!myDesktopLoansButton) auxData = auxData.filter((item) => item.path !== paths.loans);
        if (pendingCommunicationsAmount === 0)
            auxData = auxData.filter((item) => item.name !== getLocalizedString("communicationsToAttend"));

        auxData = auxData.filter((item) => {
            if (!item.path) return true;
            return availablePaths.includes(item.path);
        });

        return auxData.slice(0, 18);
    }, [dataByRole, myDesktopLoansButton, mostVisitedPages, availablePaths, userRole, pendingCommunicationsAmount]);

    const handleOnClick = (item) => {
        if (item?.onClick) {
            item.onClick();
            return;
        }

        switch (item.path) {
            case paths.myCertificates: {
                handleOpenMyCertificatesDialog();
                break;
            }
            case paths.myReceipts: {
                handleOpenMyReceiptsDialog();
                break;
            }
            case paths.digitalDocuments: {
                handleOpenMyDigitalDocumentsDialog();
                break;
            }
            case paths.requestDocuments: {
                handleOpenRequestDocumentsDialog();
                break;
            }
            case paths.myVacations: {
                handleOpenMyVacationsDialog();
                break;
            }
            default: {
                history.push(item.path);
                break;
            }
        }
    };

    return (
        <Grid container>
            {dataToUse.map((item, index) => {
                const hasCounter = typeof item?.amount === "number" && item?.amount !== 0;

                return (
                    <Grid key={index} item xs={12} sm={6} md={4} lg={2} style={{ padding: 10 }}>
                        <Tooltip title={item.name}>
                            <Grid
                                onClick={() => handleOnClick(item)}
                                container
                                item
                                alignItems="center"
                                justify="center"
                                className="myDesktopShadow easyAccessItem"
                                style={{
                                    background: "var(--secondaryBackgroundColor)",
                                    borderRadius: 7,
                                    height: 110,
                                    cursor: "pointer",
                                    position: "relative",
                                }}
                            >
                                {hasCounter && (
                                    <div className="easyAccessCounter">
                                        <Typography
                                            style={{
                                                color: "var(--secondaryBackgroundColor)",
                                                fontSize: 12,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {item.amount > 9 ? "+9" : item.amount}
                                        </Typography>
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {item.icon}
                                    <Typography className="whiteText" align="center" style={{ padding: 10 }}>
                                        {item.name}
                                    </Typography>
                                </div>
                            </Grid>
                        </Tooltip>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default EasyAccess;
